import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { replacementAPI } from '../services/api';
import { toast } from 'sonner';
import {
  RefreshCw,
  Download,
  Filter,
  Truck,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  Clock,
  ChevronRight,
  Search,
  ArrowUpDown
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';

const ReplacementPlanPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('priority');
  const [searchText, setSearchText] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterType !== 'all') params.vehicle_type = filterType;
      if (filterStatus !== 'all') params.status_filter = filterStatus;
      params.sort_by = sortBy;

      const [recordsRes, statsRes] = await Promise.all([
        replacementAPI.list(params),
        replacementAPI.getStatistics(),
      ]);

      setRecords(recordsRes.data || []);
      setStatistics(statsRes.data || {});
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load replacement plan data');
    } finally {
      setLoading(false);
    }
  }, [filterType, filterStatus, sortBy]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '-';
    return num.toLocaleString();
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      critical: { color: 'bg-red-100 text-red-800 border-red-200', label: 'Critical' },
      exceeded: { color: 'bg-red-600 text-white', label: 'Exceeded' },
      warning: { color: 'bg-amber-100 text-amber-800 border-amber-200', label: 'Warning' },
      high: { color: 'bg-orange-100 text-orange-800 border-orange-200', label: 'High' },
      medium: { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', label: 'Medium' },
      attention: { color: 'bg-blue-100 text-blue-800 border-blue-200', label: 'Attention' },
      low: { color: 'bg-blue-50 text-blue-700 border-blue-200', label: 'Low' },
      ok: { color: 'bg-emerald-100 text-emerald-800 border-emerald-200', label: 'OK' },
    };
    const config = statusConfig[status] || statusConfig.ok;
    return <Badge variant="outline" className={config.color}>{config.label}</Badge>;
  };

  const filteredRecords = records.filter((record) => {
    if (!searchText) return true;
    const search = searchText.toLowerCase();
    return (
      record.registration_number?.toLowerCase().includes(search) ||
      record.make?.toLowerCase().includes(search) ||
      record.model?.toLowerCase().includes(search)
    );
  });

  const handleExport = () => {
    replacementAPI.exportExcel();
  };

  return (
    <div className="space-y-6" data-testid="replacement-plan-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Replacement Plan</h1>
          <p className="text-slate-500">Track vehicle and trailer replacement schedules</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="outline" onClick={handleExport}>
            <Download className="w-4 h-4 mr-2" />
            Export Excel
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      {statistics && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Total Trucks</p>
              <p className="text-2xl font-bold">{statistics.total_vehicles}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Total Trailers</p>
              <p className="text-2xl font-bold">{statistics.total_trailers}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-red-500">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Trucks Critical</p>
              <p className="text-2xl font-bold text-red-600">{statistics.trucks_critical}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-amber-500">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Trucks Warning</p>
              <p className="text-2xl font-bold text-amber-600">{statistics.trucks_warning}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-red-400">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Trailers Critical</p>
              <p className="text-2xl font-bold text-red-600">{statistics.trailers_critical}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-amber-400">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Trailers Warning</p>
              <p className="text-2xl font-bold text-amber-600">{statistics.trailers_warning}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-slate-500" />
            <span className="font-medium text-sm">Filters</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Vehicle Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="truck">Trucks Only</SelectItem>
                <SelectItem value="trailer">Trailers Only</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
                <SelectItem value="warning">Warning</SelectItem>
                <SelectItem value="attention">Attention</SelectItem>
                <SelectItem value="ok">OK</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Sort By" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="priority">Priority (Urgent First)</SelectItem>
                <SelectItem value="km_remaining">KM Remaining</SelectItem>
                <SelectItem value="age_remaining">Age Remaining</SelectItem>
                <SelectItem value="registration">Registration</SelectItem>
              </SelectContent>
            </Select>
            <div className="relative col-span-2">
              <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
              <Input
                placeholder="Search registration, make, model..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="h-9 pl-8"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Truck className="w-6 h-6 text-blue-600 mt-1" />
              <div>
                <h3 className="font-semibold text-blue-900">Truck Replacement Thresholds</h3>
                <ul className="text-sm text-blue-700 mt-1 space-y-1">
                  <li>• Review at <strong>600,000 km</strong> odometer reading</li>
                  <li>• Review at <strong>5 years</strong> of age</li>
                  <li>• KM warnings: 100K, 50K, 30K, 10K km remaining</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Truck className="w-6 h-6 text-purple-600 mt-1" />
              <div>
                <h3 className="font-semibold text-purple-900">Trailer Replacement Thresholds</h3>
                <ul className="text-sm text-purple-700 mt-1 space-y-1">
                  <li>• Review at <strong>10 years</strong> of age</li>
                  <li>• Age warnings: 1 year, 6 months, 90/60/30 days remaining</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Data Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs font-semibold">Registration</TableHead>
                  <TableHead className="text-xs font-semibold">Type</TableHead>
                  <TableHead className="text-xs font-semibold">Make / Model</TableHead>
                  <TableHead className="text-xs font-semibold text-center">Year Model</TableHead>
                  <TableHead className="text-xs font-semibold text-center">Age (Years)</TableHead>
                  <TableHead className="text-xs font-semibold text-right">Current KM</TableHead>
                  <TableHead className="text-xs font-semibold text-right">KM Remaining</TableHead>
                  <TableHead className="text-xs font-semibold text-center">KM Status</TableHead>
                  <TableHead className="text-xs font-semibold text-center">Days to Age Limit</TableHead>
                  <TableHead className="text-xs font-semibold text-center">Age Status</TableHead>
                  <TableHead className="text-xs font-semibold text-center">Overall</TableHead>
                  <TableHead className="text-xs font-semibold">CA Status</TableHead>
                  <TableHead className="text-xs font-semibold">Latest Comment</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRecords.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={13} className="text-center py-8">
                      <Truck className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                      <p className="text-slate-500">No vehicles found</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRecords.map((record) => {
                    const isCritical = record.overall_status === 'critical' || record.overall_status === 'exceeded';
                    const isWarning = record.overall_status === 'warning' || record.overall_status === 'high';
                    
                    return (
                      <TableRow
                        key={record.id}
                        className={`${isCritical ? 'bg-red-50' : isWarning ? 'bg-amber-50' : ''} hover:bg-slate-50`}
                      >
                        <TableCell className="font-mono font-medium">{record.registration_number}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={record.is_trailer ? 'bg-purple-50' : 'bg-blue-50'}>
                            {record.is_trailer ? 'Trailer' : 'Truck'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">{record.make} {record.model}</TableCell>
                        <TableCell className="text-center">{record.year_model}</TableCell>
                        <TableCell className="text-center font-medium">{record.age_years}</TableCell>
                        <TableCell className="text-right font-mono">
                          {record.is_trailer ? '-' : formatNumber(record.current_km)}
                        </TableCell>
                        <TableCell className={`text-right font-mono font-medium ${
                          record.km_remaining !== null && record.km_remaining <= 30000 ? 'text-red-600' :
                          record.km_remaining !== null && record.km_remaining <= 100000 ? 'text-amber-600' : ''
                        }`}>
                          {record.km_remaining !== null ? formatNumber(record.km_remaining) : '-'}
                        </TableCell>
                        <TableCell className="text-center">
                          {record.km_status ? getStatusBadge(record.km_status) : '-'}
                        </TableCell>
                        <TableCell className={`text-center font-medium ${
                          record.days_until_age_threshold <= 90 ? 'text-red-600' :
                          record.days_until_age_threshold <= 365 ? 'text-amber-600' : ''
                        }`}>
                          {record.days_until_age_threshold <= 0 ? (
                            <span className="text-red-600 font-bold">OVERDUE</span>
                          ) : (
                            formatNumber(record.days_until_age_threshold)
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {getStatusBadge(record.age_status)}
                        </TableCell>
                        <TableCell className="text-center">
                          {getStatusBadge(record.overall_status)}
                        </TableCell>
                        <TableCell>
                          {record.corrective_action_status ? (
                            <Badge 
                              variant="outline" 
                              className={
                                record.corrective_action_status === 'closed' 
                                  ? 'bg-emerald-100 text-emerald-800' 
                                  : record.corrective_action_status === 'in_progress'
                                    ? 'bg-blue-100 text-blue-800'
                                    : 'bg-amber-100 text-amber-800'
                              }
                              title={record.corrective_action_title}
                            >
                              {record.corrective_action_status === 'closed' ? 'Closed' : 
                               record.corrective_action_status === 'in_progress' ? 'In Progress' : 'Open'}
                            </Badge>
                          ) : (
                            <span className="text-slate-400 text-xs">-</span>
                          )}
                        </TableCell>
                        <TableCell className="max-w-[200px]">
                          {record.latest_comment ? (
                            <span 
                              className="text-xs text-slate-600 truncate block" 
                              title={record.latest_comment}
                            >
                              {record.latest_comment}
                            </span>
                          ) : (
                            <span className="text-slate-400 text-xs">No comments</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Legend */}
      <Card>
        <CardContent className="p-4">
          <h3 className="font-semibold text-sm mb-3">Status Legend</h3>
          <div className="flex flex-wrap gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-red-500"></div>
              <span>Critical / Exceeded - Immediate review required</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-amber-500"></div>
              <span>Warning - Review soon (30-90 days / 30-50K km)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-blue-500"></div>
              <span>Attention - Plan ahead (100K km / 1 year)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-emerald-500"></div>
              <span>OK - Within acceptable range</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ReplacementPlanPage;
